import i18next from "i18next";
import type { ResourceKey } from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import resourcesToBackend from "i18next-resources-to-backend";
import { FALLBACK_LNG, NAMESPACES, NS, SUPPORTED_LNGS } from "./namespaces";

/**
 * Glob backend: import.meta.glob discovers every locale JSON under src at build time and
 * loads the one i18next asks for on demand. `import: "default"` unwraps the JSON module.
 */
const localeLoaders = import.meta.glob<ResourceKey>("../../locales/*/*.json", {
  import: "default",
});

/**
 * Configure and initialize the (default) i18next instance. Returns the init promise so the
 * app can gate its first render on it (main.tsx) — first paint never shows raw keys.
 *
 * The instance is NOT initialized at import time; only calling this runs the side effect,
 * which keeps the i18n singleton off the side-effect-free @core root barrel.
 */
export function initI18n(): Promise<unknown> {
  return i18next
    .use(LanguageDetector)
    .use(
      resourcesToBackend((language: string, namespace: string) => {
        const loader =
          localeLoaders[`../../locales/${language}/${namespace}.json`];
        if (!loader) {
          return Promise.reject(
            new Error(`Missing locale resource: ${language}/${namespace}`)
          );
        }
        return loader();
      })
    )
    .use(initReactI18next)
    .init({
      fallbackLng: FALLBACK_LNG,
      supportedLngs: SUPPORTED_LNGS,
      load: "languageOnly", // normalize fr-FR → fr
      ns: NAMESPACES,
      defaultNS: NS.COMMON,
      fallbackNS: NS.ERRORS, // lets dotted "errors.<kind>" keys resolve in the errors ns
      returnNull: false,
      returnEmptyString: false,
      interpolation: { escapeValue: false }, // React already escapes
      detection: {
        // French is the default: no browser-language or page-lang detection, so a first-time
        // visitor always gets French. A user's explicit switch is remembered in localStorage and
        // wins; once signed in, the language saved on the account wins (PreferredLanguageSync).
        order: ["localStorage"],
        caches: ["localStorage"],
        // v4: rotating the key invalidates caches written under the previous default (English),
        // so nobody keeps an English choice they never made. Bump again if the default rule
        // ever changes.
        lookupLocalStorage: "dz-lang-v4",
      },
    });
}

export { default as i18n } from "i18next";
export { NS, NAMESPACES, SUPPORTED_LNGS, FALLBACK_LNG } from "./namespaces";
export type { AppLanguage } from "./namespaces";
